import { Button, Dialog, Text } from '@risksmart-app/atomic-ui';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { WebhookPart } from 'src/contracts/querySelections';

interface Props {
  /** The webhook to resume, or null when the modal is closed. */
  webhook: WebhookPart | null;
  loading: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

/**
 * Confirmation dialog for resuming a paused or failed webhook. Resuming flips
 * the webhook back to `active`; past failed events are not replayed (the copy
 * makes this explicit).
 */
const ResumeWebhookModal: FC<Props> = ({
  webhook,
  loading,
  onConfirm,
  onDismiss,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'webhooks.resume_modal',
  });

  return (
    <Dialog
      open={webhook !== null}
      onOpenChange={(open) => {
        // Suppress overlay/Esc dismissal while the resume call is in flight so
        // it can't be triggered twice.
        if (!open && !loading) {
          onDismiss();
        }
      }}
      size={'md'}
    >
      <Dialog.Header title={t('title')} />
      <Dialog.Body>
        <Text preset={'body-md'}>{t('body')}</Text>
      </Dialog.Body>
      <Dialog.Footer>
        <Button variant={'default'} onClick={onConfirm} disabled={loading}>
          {t('confirm_button')}
        </Button>
        <Button
          variant={'neutral'}
          style={'outline'}
          onClick={onDismiss}
          disabled={loading}
        >
          {t('cancel_button')}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};

export default ResumeWebhookModal;
